import { useMemo, useState } from 'react';
import { I18n } from '@aws-amplify/core';
import { CATEGORY_QUESTIONS_KEY_TEXT } from '../contants';
import { Risk } from '@/components/views/Resolution886/enums';
import { useResolution886Report } from '@/context/Resolution886Report';
import { useGetResolution886AssessmentCategoryResult } from '@/hooks/v2/resolution-886';
import { useGetResolution886CategoryQuestion } from '@/hooks/v2/resolution-886/useGetResolution886CategoryQuestion';
import { StepsNames } from '@/context/Resolution886Report/types';

enum FormQuestions {
	MANDATORY_QUESTIONS = 'mandatory_questions',
	CONDITIONAL_QUESTIONS = 'conditional_questions'
}

interface EvaluationCategoriesProps {
	goToSubStep: (step: number) => void;
}

export function useEvaluationCategories({ goToSubStep }: EvaluationCategoriesProps) {
	const {
		form,
		assessment,
		newReview,
		previousReview,
		handleSetStepsNames,
		setCategoryId,
		handleCurrentStep,
		handleSetCategoryResultId
	} = useResolution886Report();

	const [activeKey, setActiveKey] = useState<string | string[]>([]);

	const organization_id = form.getFieldValue(['general_information', 'organization_id']);
	const company_id = form.getFieldValue(['general_information', 'company_id']);
	const review_id = form.getFieldValue(['general_information', 'review_id']);
	const report_id = form.getFieldValue(['id']);
	const assessment_id = assessment.id;
	const activity_id = assessment.activity?.id || '';

	const { isFetching: isFetchingAssessmentCategory, data: assessmentCategory } =
		useGetResolution886AssessmentCategoryResult({
			organization_id,
			company_id,
			review_id,
			report_id,
			assessment_id,
			activity_id
		});

	const { isFetching: isFetchingPreviousAssessmentCategory, data: previousAssessmentCategory } =
		useGetResolution886AssessmentCategoryResult({
			organization_id,
			company_id,
			report_id,
			activity_id,
			review_id: (previousReview && previousReview?.id) || ''
		});

	const { isFetching: isFetchingCategoryQuestions, data: categoryQuestions } = useGetResolution886CategoryQuestion({
		assessment_id,
		company_id,
		organization_id,
		report_id,
		review_id
	});

	const { data: previousCategoryQuestions } = useGetResolution886CategoryQuestion({
		company_id,
		organization_id,
		report_id,
		activity_id,
		review_id: (previousReview && previousReview?.id) || ''
	});

	const previousCategories = useMemo(
		() => [
			{
				id: 'lift_and_lower',
				name: I18n.get('Lift and lower'),
				status: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'lift_and_lower'
				)?.is_avaliated,
				risk: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'lift_and_lower'
				)?.risk
			},
			{
				id: 'push_pull',
				name: I18n.get('Push / pull'),
				status: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'push_pull'
				)?.is_avaliated,
				risk: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'push_pull'
				)?.risk
			},
			{
				id: 'transport',
				name: I18n.get('Transport'),
				status: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'transport'
				)?.is_avaliated,
				risk: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'transport'
				)?.risk
			},
			{
				id: 'standing',
				name: I18n.get('Standing'),
				status: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'standing'
				)?.is_avaliated,
				risk: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'standing'
				)?.risk
			},
			{
				id: 'repetitive_movements',
				name: I18n.get('Repetitive movements'),
				status: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'repetitive_movements'
				)?.is_avaliated,
				risk: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'repetitive_movements'
				)?.risk
			},
			{
				id: 'forced_posture',
				name: I18n.get('Forced posture'),
				status: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'forced_posture'
				)?.is_avaliated,
				risk: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'forced_posture'
				)?.risk
			},
			{
				id: 'hand_arm_vibration',
				name: I18n.get('Hand-arm vibration'),
				status: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'hand_arm_vibration'
				)?.is_avaliated,
				risk: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'hand_arm_vibration'
				)?.risk
			},
			{
				id: 'whole_body_vibration',
				name: I18n.get('Whole body vibration'),
				status: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'whole_body_vibration'
				)?.is_avaliated,
				risk: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'whole_body_vibration'
				)?.risk
			},
			{
				id: 'thermal_comfort',
				name: I18n.get('Thermal comfort'),
				status: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'thermal_comfort'
				)?.is_avaliated,
				risk: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'thermal_comfort'
				)?.risk
			},
			{
				id: 'contact_stress',
				name: I18n.get('Contact stress'),
				status: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'contact_stress'
				)?.is_avaliated,
				risk: previousAssessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'contact_stress'
				)?.risk
			}
		],
		[previousAssessmentCategory]
	);

	const categories = useMemo(
		() => [
			{
				id: 'lift_and_lower',
				name: I18n.get('Lift and lower'),
				status: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'lift_and_lower'
				)?.is_avaliated,
				risk: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'lift_and_lower'
				)?.risk
			},
			{
				id: 'push_pull',
				name: I18n.get('Push / pull'),
				status: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'push_pull'
				)?.is_avaliated,
				risk: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'push_pull'
				)?.risk
			},
			{
				id: 'transport',
				name: I18n.get('Transport'),
				status: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'transport'
				)?.is_avaliated,
				risk: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'transport'
				)?.risk
			},
			{
				id: 'standing',
				name: I18n.get('Standing'),
				status: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'standing'
				)?.is_avaliated,
				risk: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'standing'
				)?.risk
			},
			{
				id: 'repetitive_movements',
				name: I18n.get('Repetitive movements'),
				status: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'repetitive_movements'
				)?.is_avaliated,
				risk: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'repetitive_movements'
				)?.risk
			},
			{
				id: 'forced_posture',
				name: I18n.get('Forced posture'),
				status: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'forced_posture'
				)?.is_avaliated,
				risk: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'forced_posture'
				)?.risk
			},
			{
				id: 'hand_arm_vibration',
				name: I18n.get('Hand-arm vibration'),
				status: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'hand_arm_vibration'
				)?.is_avaliated,
				risk: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'hand_arm_vibration'
				)?.risk
			},
			{
				id: 'whole_body_vibration',
				name: I18n.get('Whole body vibration'),
				status: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'whole_body_vibration'
				)?.is_avaliated,
				risk: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'whole_body_vibration'
				)?.risk
			},
			{
				id: 'thermal_comfort',
				name: I18n.get('Thermal comfort'),
				status: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'thermal_comfort'
				)?.is_avaliated,
				risk: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'thermal_comfort'
				)?.risk
			},
			{
				id: 'contact_stress',
				name: I18n.get('Contact stress'),
				status: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'contact_stress'
				)?.is_avaliated,
				risk: assessmentCategory?.reviews[0]?.assessments[0]?.categories.find(
					(category) => category.key === 'contact_stress'
				)?.risk
			}
		],
		[assessmentCategory]
	);

	const categoriesStepsNames = [
		{
			key: 'lift_and_lower',
			steps: {
				exposure_time: 'lift_lower_exposure_time',
				mandatory_questions: 'lift_lower_identify_task',
				conditional_questions: 'lift_lower_risk_level'
			}
		},
		{
			key: 'push_pull',
			steps: {
				exposure_time: 'push_pull_exposure_time',
				mandatory_questions: 'push_pull_identify_task',
				conditional_questions: 'push_pull_risk_level'
			}
		},
		{
			key: 'transport',
			steps: {
				exposure_time: 'transport_exposure_time',
				mandatory_questions: 'transport_identify_task',
				conditional_questions: 'transport_risk_level'
			}
		},
		{
			key: 'standing',
			steps: {
				exposure_time: 'standing_exposure_time',
				mandatory_questions: 'standing_identify_task',
				conditional_questions: 'standing_risk_level'
			}
		},
		{
			key: 'repetitive_movements',
			steps: {
				exposure_time: 'repetitive_exposure_time',
				mandatory_questions: 'repetitive_identify_task',
				conditional_questions: 'repetitive_risk_level'
			}
		},
		{
			key: 'forced_posture',
			steps: {
				exposure_time: 'forced_posture_exposure_time',
				mandatory_questions: 'forced_posture_identify_task',
				conditional_questions: 'forced_posture_risk_level'
			}
		},
		{
			key: 'hand_arm_vibration',
			steps: {
				exposure_time: 'hand_arm_vibration_exposure_time',
				mandatory_questions: 'hand_arm_vibration_identify_task',
				conditional_questions: 'hand_arm_vibration_risk_level'
			}
		},
		{
			key: 'whole_body_vibration',
			steps: {
				exposure_time: 'whole_body_vibration_exposure_time',
				mandatory_questions: 'whole_body_vibration_identify_task',
				conditional_questions: 'whole_body_vibration_risk_level'
			}
		},
		{
			key: 'thermal_comfort',
			steps: {
				exposure_time: 'thermal_comfort_exposure_time',
				mandatory_questions: 'thermal_comfort_identify_task',
				conditional_questions: 'thermal_comfort_risk_level'
			}
		},
		{
			key: 'contact_stress',
			steps: {
				exposure_time: 'contact_stress_exposure_time',
				mandatory_questions: 'contact_stress_identify_task',
				conditional_questions: 'contact_stress_risk_level'
			}
		}
	];

	function handleCategoryClick(categoryId: string): void {
		form.resetFields(['questions']);

		if (categoryQuestions) {
			mapCategoryQuestion(categoryQuestions, categoryId);
		}

		if (previousCategoryQuestions) {
			mapPreviousCategoryQuestion(previousCategoryQuestions, categoryId);
		}

		setCategoryId(categoryId);
		handleSetStepsNames(categoriesStepsNames.find((category) => category.key === categoryId) as StepsNames);
		goToSubStep(3);
	}

	function setRiskName(risk: Risk): string {
		if (risk === Risk.MODERATE) {
			return I18n.get('Moderate risk');
		}

		if (risk === Risk.NOT_TOLERABLE) {
			return I18n.get('Not tolerable risk');
		}

		return I18n.get('Tolerable risk');
	}

	function setFormQuestions(result: any, type: FormQuestions) {
		const questions = Object.assign(
			result.questions_results.map((question: any) => {
				return {
					[question.question.name]: question.value.toLowerCase()
				};
			})
		);

		const questionsObject = questions.reduce((acc: any, curr: any) => {
			return { ...acc, ...curr };
		}, {});

		form.setFieldValue(['questions', type], questionsObject);
	}

	function mapCategoryQuestion(categoryQuestions: any, categoryId: string) {
		const results = categoryQuestions[0].assessments[0].category_results.find(
			(item: any) => item.category.key === categoryId
		);

		handleSetCategoryResultId(results?.id ? results.id : '');

		for (const result of results.steps_results) {
			if (!result.id) {
				return;
			}

			if (result.step.name.includes('exposure_time')) {
				form.setFieldValue(['questions', 'exposure_time'], result.value);
			}

			if (result.step.name.includes('identify_task')) {
				setFormQuestions(result, FormQuestions.MANDATORY_QUESTIONS);
			}

			if (result.step.name.includes('risk_level')) {
				setFormQuestions(result, FormQuestions.CONDITIONAL_QUESTIONS);
			}
		}
	}

	function mapPreviousCategoryQuestion(categoryQuestions: any, categoryId: string) {
		form.resetFields(['questions_previous']);

		const results = categoryQuestions[0].assessments[0].category_results.find(
			(item: any) => item.category.key === categoryId
		);

		for (const result of results.steps_results) {
			if (result.step.name.includes('exposure_time')) {
				form.setFieldValue(['questions_previous', 'exposure_time'], result.value ? result.value : '0');
			}

			if (result.step.name.includes('identify_task')) {
				setPreviousFormQuestions(result, FormQuestions.MANDATORY_QUESTIONS);
			}

			if (result.step.name.includes('risk_level')) {
				setPreviousFormQuestions(result, FormQuestions.CONDITIONAL_QUESTIONS);
			}
		}
	}

	function setPreviousFormQuestions(result: any, type: FormQuestions) {
		interface Item {
			sequence: number;
			question: string;
			response: 'yes' | 'no' | null;
		}

		const items: Item[] = [];

		for (const item of result.questions_results) {
			const keyText = CATEGORY_QUESTIONS_KEY_TEXT.find((cat) => cat.key === item.question.name);

			items.push({
				sequence: keyText!.sequence,
				question: keyText!.text,
				response: item.value === null ? 'no' : (String(item.value).toLowerCase() as 'yes' | 'no')
			});
		}

		form.setFieldValue(
			['questions_previous', type],
			items.sort((a, b) => a.sequence - b.sequence)
		);
	}

	function handleOnToggleCollapse(): void {
		setActiveKey(activeKey.length ? [] : ['1']);
	}

	const isLoading =
		isFetchingAssessmentCategory || isFetchingCategoryQuestions || isFetchingPreviousAssessmentCategory;

	return {
		activeKey,
		categories,
		assessment,
		newReview,
		previousReview,
		previousCategories,
		isLoading,
		handleCategoryClick,
		handleCurrentStep,
		setRiskName,
		handleOnToggleCollapse
	};
}
